import { RopRateWithholdingEnum } from 'libs/modules/tunnelsante-tunnel/rop/models/rop';
import { AddressModels } from './address.models';
import { CommonModels } from './common.models';
import { HealthModels } from './health.models';
import { TableModels } from './table.models';
import ScreenType = CommonModels.ScreenType;
import ProductDocumentationCategory = CommonModels.ProductDocumentationCategory;
import RenewalBehavior = CommonModels.RenewalBehavior;

export interface ProductDocumentation {
  id: number;
  name: string;
  description: string;
  url: string;
  category: ProductDocumentationCategory;
  creationDate: Date;
  lastModified: Date;
  file: File;
  order: number;
  screen: ScreenType;
  screenValue: string;
  catValue: string;
  isNew: boolean;
  isInternal: boolean;
}

export interface EmailKeyPhrase {
  id: number;
  keyPhrase: string;
  emailMessageQueueId?: number;
  emailMessageQueue?: string;
  priority?: number;
  lastModified: string;
  disabled: boolean;
}

export interface blacklistedPackage {
  id: number;
  renewalBehavior: RenewalBehavior;
}

export namespace AgencyModels {
  import FaqCategory = CommonModels.FaqCategory;
  import ContactCategory = CommonModels.ContactCategory;
  import InsuranceTypeEnum = CommonModels.InsuranceTypeEnum;

  export interface Agency {
    id: number;
    name: string;
    siret?: string;
    manager?: string;
  }

  export interface Faq {
    id: number;
    name: string;
    question: string;
    answer: string;
    category: number;
    catValue: string;
    creationDate: Date;
    lastModified: Date;
    isNew: boolean;
  }

  export interface EmailMessageQueue {
    id: number;
    name: string;
    insuranceType?: InsuranceTypeEnum;
    emailKeyPhrases?: EmailKeyPhrase[];
    agents?: string[];
  }

  export interface Contact {
    id: number;
    name: string;
    email: string;
    phone: string;
    address1?: string;
    address2?: string;
    category: ContactCategory;
    catValue: string;
    order: number;
    title: number;
    creationDate: Date;
    lastModified: Date;
    faqCategory: FaqCategory;
    faqCategoryVal: string;
  }

  export interface SubBrokerTableRow {
    subBrokerId: number;
    login: string;
    creationDate: Date;
    policiesCount: number;
  }

  export interface PatchAgencyRequest {
    headOffice?: HeadOfficeForm;
    headOfficeLegal?: HeadOfficeLegalForm;
    headOfAgency?: HeadOfAgencyForm;
    productAdministration?: ProductAdministrationForm;
  }

  export interface AgencySlipTableRow {
    id: number;
    agencyId: number;
    isPaid: boolean;
    fileUrl: string;
    excelFileUrl: string;
    creationDate: Date;
    insuranceTypeId: number;
  }
  export interface AgencyEmailsPaginatedRequest
    extends TableModels.PaginatedRequest {
    search: string;
  }

  export interface AgenciesPaginatedRequest
    extends TableModels.PaginatedRequest {
    agencyName: string;
  }

  export interface AgencyTableRow {
    agencyId: number;
    publicId: string;
    state: CommonModels.AgencyStatusEnum;
    name: string;
    companyType: CommonModels.CompanyTypeEnum;
    creationDate: Date;
    headOfAgency: string;
  }

  export interface SubscriptionTableRow {
    agencyId: number;
    name: string;
    contact: string;
    creationDate: Date;
    kbis: CommonModels.StatusEnum;
    orias: CommonModels.StatusEnum;
    rib: CommonModels.StatusEnum;
  }

  export interface Manager {
    managerId: number;
    accountId: number;
    firstName: string;
    lastName: string;
  }
  export interface HeadOfficeForm {
    name: string;
    numSiret: string;
    numSiren: string;
    companyType: CommonModels.CompanyTypeEnum;
  }

  export interface ProductAdministrationForm {
    productRights: number[];
    infinityCommission: HealthCommissionProductAdministrationForm;
    fidelityCommission: HealthCommissionProductAdministrationForm;
    acheelCommission: HealthCommissionProductAdministrationForm;
    autoRates: AutoRatesProductAdministration;
    ropRates: RopRatesProductAdministration;
  }

  export interface HealthCommissionProductAdministrationForm {
    commissions: HealthModels.HealthRateCommissionPairsEnum;
    withHoldingMonthlyThreshold: number;
  }

  export interface RopRatesProductAdministration {
    withholding: RopRateWithholdingEnum;
    withHoldingMonthlyThreshold: number;
  }

  export interface AutoRatesProductAdministration {
    monthsForFirstPayment: number;
    maxFees?: number;
    maxLoading?: number;
    maxCommission?: number;
    hasFixedBrokerData?: boolean;
    minFees?: number;
  }

  export interface HeadOfficeLegalForm {
    turnover: number;
    companyCreationDate: Date;
    employeeCount: number;
    outletCount: number;
  }

  export interface PrincipalBrokerForm {
    id: number;
    ageoId: string;
    brokerLastName: string;
    brokerFirstName: string;
    brokerEmail: string;
  }

  export class HeadOfAgencyForm {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    function?: string;
    civility?: CommonModels.CivilityEnum;

    public static get empty(): HeadOfAgencyForm {
      return {
        firstName: '',
        lastName: '',
        function: '',
        email: '',
        phone: '',
        mobile: '',
        civility: CommonModels.CivilityEnum.Homme,
      };
    }
  }

  export interface AgencyDetails {
    fidelityWithholdingMetrics: WithholdingMetrics;
    acheelWithholdingMetrics: WithholdingMetrics;
    infinityWithholdingMetrics: WithholdingMetrics;
    ropWithholdingMetrics: WithholdingMetrics;
    attestationUrl: string;
    waitingDocuments: number;
    canValidateContract: boolean;
    agencyStatus: CommonModels.AgencyStatusEnum;
    headOfAgency: HeadOfAgencyForm;
    headOffice: HeadOfficeForm;
    headOfficeLegal: HeadOfficeLegalForm;
    mainBroker: PrincipalBrokerForm;
    productAdministration: ProductAdministrationForm;
    address: AddressModels.Address;
    publicId: string;
    retentions?: Retention[];
    specificities?: Specificity[];
    minInsertDate: string;
    healthSettings: HealthSetting;
    autoSettings: AutoSetting;
  }

  export interface WithholdingMetrics {
    total: number;
    monthly: number;
  }

  export interface AgencyPolicyMetrics {
    contracts: number;
    cancellations: number;
    commissions: number;
  }

  export interface GenerateSubBrokersRequest {
    amountToCreate: number;
  }

  export interface ApiCredentials {
    id: number;
    login: string;
    password: string;
  }

  export class Retention {
    id?: number;
    rate?: number;
    effectiveDate?: string;
    retentionPeriod?: string;
    isReadOnly?: boolean;
  }

  export class Specificity {
    id?: number;
    type?: number;
    date?: string;
    comment?: string;
    amount?: number;
    isReadOnly?: boolean;
    insuranceTypeId?: InsuranceTypeEnum;
  }

  export class HealthSetting {
    disableSmsForElectronicSignature?: boolean;
    disableSmsForPolicyNotification?: boolean;
    isVip?: boolean;
    allowDateEffetInPast?: boolean;
    allowDateEffetInPastNbDays?: number;
    fixedFees?: number;
    minFeesFidelity?: number;
    minFeesInfinity?: number;
    minFeesAcheel?: number;
    hasMinFeesFidelity?: boolean;
    hasMinFeesInfinity?: boolean;
    hasMinFeesAcheel?: boolean;
    quotationEmailCourtierExcluded?: boolean;
    healthSlipDisabledDate?: Date;
    emailHealthSlip?: string;
    healthContractPrintDate?: Date;
    fixFees?: boolean;
    blockSlip?: boolean;
    printContract?: boolean;
    hasCodePromo?: boolean;
    codePromoValue?: string;
  }

  export class AutoSetting {
    blackLists?: blacklistedPackage[];
  }

  export interface AgencyRdv {
    reasons: CommonModels.RdvReasonEnum[];
    otherReasonText: string;
    periods: CommonModels.RdvPeriodEnum[];
    cabinetName: string;
    fullName: string;
    email: string;
    mobileNumber: string;
    codePostal: string;
    comments?: string;
    responsableEmail: string;
  }
}
